import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';
import { ClipboardList, Info, Search } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { usePermission } from '../../hooks/usePermission';
import {
  GET_STAFFING_REQUIREMENTS_MATRIX,
  SAVE_STAFFING_REQUIREMENTS_MUTATION,
} from '../../graphql/queries';
import type { StaffingRequirement, StaffingRequirementsMatrix } from './types';
import { errorMessage } from './planningUtils';
import { RequirementsPositionCard } from './RequirementsPositionCard';

type MatrixData = { staffingRequirementsMatrix: StaffingRequirementsMatrix };

export const StaffingRequirementsPage: React.FC = () => {
  const canEdit = usePermission('planning.requirements');
  const [search, setSearch] = useState('');

  const { data, loading, error, refetch } = useQuery<MatrixData>(GET_STAFFING_REQUIREMENTS_MATRIX, {
    fetchPolicy: 'cache-and-network',
  });
  const [saveRequirements] = useMutation<{ saveStaffingRequirements: StaffingRequirement[] }>(
    SAVE_STAFFING_REQUIREMENTS_MUTATION,
  );

  const matrix = data?.staffingRequirementsMatrix;

  const slotById = useMemo(() => new Map((matrix?.slots ?? []).map((s) => [s.id, s])), [matrix]);
  const requirementsByPosition = useMemo(() => {
    const map = new Map<string, StaffingRequirement[]>();
    for (const r of matrix?.requirements ?? []) {
      const list = map.get(r.positionId) ?? [];
      list.push(r);
      map.set(r.positionId, list);
    }
    return map;
  }, [matrix]);

  const departments = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('pl');
    const groups: Array<{ name: string; positions: StaffingRequirementsMatrix['positions'] }> = [];
    const byName = new Map<string, (typeof groups)[number]>();
    for (const p of matrix?.positions ?? []) {
      if (
        q &&
        !p.positionName.toLocaleLowerCase('pl').includes(q) &&
        !(p.departmentName ?? '').toLocaleLowerCase('pl').includes(q)
      ) {
        continue;
      }
      const name = p.departmentName || 'Bez działu';
      let g = byName.get(name);
      if (!g) {
        g = { name, positions: [] };
        byName.set(name, g);
        groups.push(g);
      }
      g.positions.push(p);
    }
    return groups;
  }, [matrix, search]);

  const handleSave = async (
    positionId: string,
    items: Array<{ shiftTimeId: string; dayOfWeek: string; minHeadcount: number; targetHeadcount: number }>,
  ) => {
    try {
      await saveRequirements({
        variables: { input: { positionId, items } },
        update: (cache, result) => {
          const saved = result.data?.saveStaffingRequirements ?? [];
          cache.updateQuery<MatrixData>({ query: GET_STAFFING_REQUIREMENTS_MATRIX }, (prev) =>
            prev
              ? {
                  staffingRequirementsMatrix: {
                    ...prev.staffingRequirementsMatrix,
                    requirements: [
                      ...prev.staffingRequirementsMatrix.requirements.filter((r) => r.positionId !== positionId),
                      ...saved,
                    ],
                  },
                }
              : prev,
          );
        },
      });
      toast.success('Zapisano wymaganą obsadę stanowiska');
      return true;
    } catch (e) {
      toast.error(errorMessage(e, 'Nie udało się zapisać wymaganej obsady'));
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <ClipboardList className="w-7 h-7 text-primary" />
          Wymagana obsada
        </h2>
        <p className="text-muted-foreground mt-2">
          Ile osób z danego stanowiska potrzeba na każdej zmianie w poszczególne dni tygodnia. „Min” to najmniej, ile
          musi być, „cel” — ile chcemy mieć. Puste pole lub 0 oznacza brak wymagań.
        </p>
      </div>

      {!canEdit && (
        <div className="flex items-start gap-2 rounded-lg border bg-muted/50 px-4 py-3 text-sm">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <span>Tylko podgląd — do zmiany wymaganej obsady potrzebne jest odpowiednie uprawnienie.</span>
        </div>
      )}

      <div className="relative w-full sm:w-80">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj stanowiska lub działu"
          aria-label="Szukaj stanowiska lub działu"
          className="pl-8 h-9"
        />
      </div>

      {!matrix && loading && <p className="py-8 text-center text-muted-foreground">Ładowanie...</p>}
      {!matrix && !loading && error && (
        <Card>
          <CardContent className="p-6 space-y-3 text-center">
            <p className="text-red-600 dark:text-red-400">
              Nie udało się wczytać danych: {errorMessage(error, 'nieznany błąd')}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Spróbuj ponownie
            </Button>
          </CardContent>
        </Card>
      )}

      {matrix && departments.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">
          {search ? 'Nic nie pasuje do wyszukiwania.' : 'Brak stanowisk z przypisanymi pracownikami.'}
        </p>
      )}

      {matrix &&
        departments.map((dept) => (
          <section key={dept.name} className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground border-b pb-1">{dept.name}</h3>
            {dept.positions.map((p) => (
              <RequirementsPositionCard
                key={p.positionId}
                position={p}
                slotById={slotById}
                requirements={requirementsByPosition.get(p.positionId) ?? []}
                canEdit={canEdit}
                onSave={handleSave}
              />
            ))}
          </section>
        ))}
    </div>
  );
};
